/* eslint-disable react/display-name */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useAsyncFn } from 'react-use';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'redux/table';
import { serializeTableFilters } from 'utils';

export default function withTable(
  {
    defaultSorting,
    defaultFiltering = {},
    allowedFilters = null,
    fetchingData,
    sortingFieldsMapping = {},
    tablePageSize,
    pageAlias,
    fetchWithoutParams = false,
  },
  defaultFetchMethod,
) {
  return WrappedComponent => {
    return props => {
      const dispatch = useDispatch();
      const {
        tableData,
        count,
        page,
        sorting,
        filtering,
        pageSize,
        forceUpdateCounter,
        fetchMethod,
        graphic,
        diagrams,
        total,
        filterOptions,
      } = useSelector(state => state.table);

      // Use ref to track previous forceUpdateCounter to prevent infinite loops
      const prevForceUpdateCounterRef = useRef(0);

      const [isFetchingTableData, getTableData] = useAsyncFn(async (fetchDataMethod, params) => {
        const data = await fetchDataMethod({
          ...params,
        });

        if (!data.error) {
          dispatch(
            actions.setTableData({
              data: data[fetchingData] || [],
              count: data.count || 0,
              graphic: data.graphic || {},
              total: data.total || {},
              diagrams: data.diagrams || {},
              filterOptions: data.filterOptions || {},
            }),
          );
        }
        // Reset needForceUpdate flag after fetch completes (success or error)
        // We don't increment forceUpdateCounter here to avoid infinite loops
        dispatch(actions.forceUpdateTable(false));
      });

      const getAllowedSortFields = () => {
        const fields = new Set(Object.values(sortingFieldsMapping));

        if (defaultSorting?.field) {
          fields.add(defaultSorting.field);
        }

        return fields;
      };

      const getRequestParams = ({ includePagination = true } = {}) => {
        const sortingObj = sorting.field ? sorting : defaultSorting;
        const allowedSortFields = getAllowedSortFields();
        const sortingParams =
          sortingObj && sortingObj.field && allowedSortFields.has(sortingObj.field)
            ? {
                [sortingObj.field]: sortingObj.dir,
              }
            : defaultSorting?.field
            ? {
                [defaultSorting.field]: defaultSorting.dir,
              }
            : {};

        const serializedFilters = serializeTableFilters(filtering);
        const filterParams = allowedFilters
          ? Object.entries(serializedFilters).reduce((result, [filterName, filterValue]) => {
              if (allowedFilters.includes(filterName)) {
                result[filterName] = filterValue;
              }
              return result;
            }, {})
          : serializedFilters;

        return {
          ...(includePagination
            ? {
                page,
                pageSize: tablePageSize || pageSize,
              }
            : {}),
          ...sortingParams,
          ...filterParams,
        };
      };

      const fetchData = alias => {
        let method = defaultFetchMethod;
        if (alias) {
          method = fetchMethod[pageAlias];
        }

        if (method) {
          const requestParams = getRequestParams();
          getTableData(method, fetchWithoutParams ? undefined : requestParams);
        } else {
          // Retry after a short delay if method is not yet available
          // This handles the case when handleForceUpdate is called before fetchMethod is set
          setTimeout(() => {
            const retryMethod = alias ? fetchMethod[pageAlias] : defaultFetchMethod;
            if (retryMethod) {
              const requestParams = getRequestParams();
              getTableData(retryMethod, fetchWithoutParams ? undefined : requestParams);
            }
          }, 150);
        }
      };

      useLayoutEffect(() => {
        dispatch(actions.clearTableFiltering());

        if (Object.keys(defaultFiltering).length > 0) {
          dispatch(actions.setTableFiltering(defaultFiltering));
        }

        if (defaultSorting) {
          dispatch(actions.setTableSorting(defaultSorting));
        }

        dispatch(actions.setTablePage(0));
      }, []);

      useEffect(() => {
        fetchData(pageAlias);
      }, [page, sorting, filtering, fetchMethod]);

      useEffect(() => {
        // Check if forceUpdateCounter increased (this is the reliable trigger)
        // Skip if counter is 0 (initial state) or if counter hasn't increased
        if (forceUpdateCounter > 0 && forceUpdateCounter > prevForceUpdateCounterRef.current) {
          prevForceUpdateCounterRef.current = forceUpdateCounter;
          fetchData(pageAlias);
        }
      }, [forceUpdateCounter]);

      useEffect(() => {
        return () => {
          dispatch(actions.cleanUpTable());
        };
      }, []);

      const handleForceUpdate = () => {
        dispatch(actions.forceUpdateTable(true));
      };

      const handleChangePage = currentPage => {
        dispatch(actions.setTablePage(currentPage));
      };

      const handleSorting = (field, dir) => {
        const fieldMapping = {
          [field]: field,
          isActive: 'active',
          ...sortingFieldsMapping,
        };
        dispatch(
          actions.setTableSorting({
            field: fieldMapping[field],
            dir,
          }),
        );
      };

      const handleFiltering = (filterName, filterValue) => {
        dispatch(
          actions.setTableFiltering({
            [filterName]: filterValue,
          }),
        );
        dispatch(actions.setTablePage(0));
      };

      const handleRemoveFilter = filterName => {
        dispatch(actions.removeTableFilter(filterName));
        dispatch(actions.setTablePage(0));
      };

      const handleClearFilters = () => {
        dispatch(actions.clearTableFiltering());
        dispatch(actions.setTablePage(0));
      };

      const handleChangeFetchMethod = (alias, method) => {
        dispatch(actions.setFetchMethod({ alias, method }));
        dispatch(actions.setTablePage(0));
      };

      return (
        <WrappedComponent
          tableData={{
            data: tableData,
            totalCount: count,
            page,
            pageSize: tablePageSize || pageSize,
            filtering,
            filterOptions,
            isFetchingTableData: isFetchingTableData.loading,
            handleChangePage,
            handleSorting,
            handleFiltering,
            handleForceUpdate,
            handleChangeFetchMethod,
            handleRemoveFilter,
            handleClearFilters,
            getRequestParams,
          }}
          diagrams={diagrams}
          graphic={graphic}
          total={total}
          {...props}
        />
      );
    };
  };
}
